/**
 * BagLock (rules v2) bag geometry, free of Phaser so it is unit-tested in node. The bag shows
 * `cap` open slots then `locked` padlocked ones at its right end; items only ever fill the open
 * ones (a glued pair's cap + 1 squeeze stays inside them). Core decides every rule.
 */
import type { RoundEvent } from '@mp/core';
import { slotStep } from './pairs.js';

export interface BagGeom {
  /** open capacity (core `r.cap`) */
  cap: number;
  /** slots still locked (core `r.locked`) */
  locked: number;
  /** slot size in px */
  slot: number;
  /** bag centre x */
  cx: number;
}

/** Slots drawn: the open ones plus the locked ones. */
export const slotsShown = (g: Pick<BagGeom, 'cap' | 'locked'>): number => g.cap + g.locked;

/** Left-most slot centre. */
const x0 = (g: BagGeom): number => g.cx - (g.slot * slotsShown(g)) / 2 + g.slot / 2;

/** Centre x of slot `i` as drawn (open or locked; never squeezed). */
export function slotX(g: BagGeom, i: number): number {
  return x0(g) + i * g.slot;
}

/** Centre x of the `k`-th item of `count` in the bag (squeezes cap + 1 items into the open slots). */
export function itemX(g: BagGeom, k: number, count: number): number {
  return x0(g) + k * slotStep(g.cap, count, g.slot);
}

/** Display indices of the locked slots (the right end of the bag). */
export function lockedSlots(g: Pick<BagGeom, 'cap' | 'locked'>): number[] {
  return Array.from({ length: g.locked }, (_, k) => g.cap + k);
}


export interface BagShape {
  cap: number;
  locked: number;
}

/**
 * The bag after an event: extra slot and rescues report the open capacity (locked slots stay
 * locked), the unlock opens them all. Null when the event leaves the bag's shape alone.
 */
export function bagShape(prev: BagShape, e: RoundEvent): BagShape | null {
  switch (e.type) {
    case 'extraSlot':
    case 'rescued':
      return { cap: e.cap, locked: prev.locked };
    case 'bagUnlocked':
      return { cap: e.cap, locked: 0 };
    default:
      return null;
  }
}
